import { ICON_STROKE, TrendingUp } from "@/shared/components/icons";
import type { SkillState } from "@/shared/models";
import { bandMeta, nextBand, overallLevel } from "@/features/skills/lib/skillStats";

export function EmptyHero() {
  return (
    <div className="rounded-xl border border-border bg-surface p-8 text-center space-y-3">
      <div className="mx-auto w-12 h-12 rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
        <TrendingUp size={24} strokeWidth={ICON_STROKE} />
      </div>
      <div className="font-semibold text-text tracking-tight">No signal yet</div>
      <p className="text-sm text-text-muted max-w-md mx-auto">
        Run a few drills and your skill profile will start filling in. The
        coach watches every spot and updates this view automatically.
      </p>
    </div>
  );
}

// 270° speedometer dial geometry (gap at the bottom).
const CX = 70;
const CY = 70;
const R = 54;
const SW = 10;
const CIRC = 2 * Math.PI * R;
const ARC = 0.75; // 270° of the circle
const ARC_LEN = ARC * CIRC;
const ROTATE = 135; // start the arc bottom-left → sweep over the top → bottom-right

/** Point on the dial for a 0-100 value (50 sits at top). */
function dialPoint(value: number): { x: number; y: number } {
  const angle = ((ROTATE + (value / 100) * 270) * Math.PI) / 180;
  return { x: CX + R * Math.cos(angle), y: CY + R * Math.sin(angle) };
}

export function OverallHero({
  skills,
  totalSignals,
}: {
  skills: SkillState[];
  totalSignals: number;
}) {
  // Confidence-weighted overall — same definition as the trajectory's
  // "overall" point, so the gauge and the chart's "Now" value agree.
  const avg = overallLevel(skills);
  const { name, color } = bandMeta(avg);
  const goal = nextBand(avg);
  const fillLen = (avg / 100) * ARC_LEN;
  const marker = dialPoint(avg);

  return (
    <section className="relative rounded-2xl border border-border bg-surface p-6 flex flex-col items-center gap-4 overflow-hidden">
      {/* Ambient glow tinted to the current band. */}
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-56 h-56 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}24 0%, transparent 60%)`, filter: "blur(14px)" }}
      />

      <div className="relative w-[clamp(120px,34vw,168px)]">
        <svg viewBox="0 0 140 140" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {/* track */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth={SW}
            strokeLinecap="round"
            strokeDasharray={`${ARC_LEN} ${CIRC}`}
            transform={`rotate(${ROTATE} ${CX} ${CY})`}
          />
          {/* filled value arc, colored by band */}
          <circle
            cx={CX}
            cy={CY}
            r={R}
            fill="none"
            stroke={color}
            strokeWidth={SW}
            strokeLinecap="round"
            strokeDasharray={`${fillLen} ${CIRC}`}
            transform={`rotate(${ROTATE} ${CX} ${CY})`}
            style={{
              transition: "stroke-dasharray 0.7s ease, stroke 0.4s ease",
              filter: `drop-shadow(0 0 6px ${color}99)`,
            }}
          />
          {/* band-boundary notches (20/40/60/80) for scale */}
          {[20, 40, 60, 80].map((v) => {
            const p = dialPoint(v);
            return (
              <circle
                key={v}
                cx={p.x}
                cy={p.y}
                r={1.6}
                fill="var(--color-bg)"
                stroke="var(--color-text-muted)"
                strokeWidth={0.8}
              />
            );
          })}
          {/* current-value marker */}
          <circle cx={marker.x} cy={marker.y} r={4} fill={color} stroke="var(--color-surface)" strokeWidth={2} />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-5xl font-bold tabular-nums tracking-tight text-text leading-none">
            {avg}
          </span>
          <span className="text-sm font-semibold mt-1.5" style={{ color }}>
            {name}
          </span>
        </div>
      </div>

      <div className="relative flex flex-col items-center gap-1.5">
        {goal ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-bg/40 px-3 py-1 text-xs text-text-muted">
            <span className="font-semibold tabular-nums text-text">{goal.remaining}</span>
            to {goal.name}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-medium" style={{ color }}>
            Top band reached
          </span>
        )}
        <span className="text-xs text-text-muted/80">
          {totalSignals} signal{totalSignals === 1 ? "" : "s"} logged across {skills.length} skill
          {skills.length === 1 ? "" : "s"}
        </span>
      </div>
    </section>
  );
}
